/**
 * ReprintInvoicesDialog Component (BLU-328)
 *
 * Confirmation dialog shown when user attempts to reprint invoices
 * for a batch that has already had invoices printed.
 * Follows ReprintConfirmDialog pattern.
 */

import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ReprintInvoicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchNumber: number;
  lastPrintedAt: string | null;
  onConfirm: () => void;
  isConfirming?: boolean;
}

export function ReprintInvoicesDialog({
  open,
  onOpenChange,
  batchNumber,
  lastPrintedAt,
  onConfirm,
  isConfirming = false,
}: ReprintInvoicesDialogProps) {
  const { t } = useTranslation('production');
  const formattedDate = lastPrintedAt
    ? new Date(lastPrintedAt).toLocaleString()
    : t('invoice.reprint.previously');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-5 w-5" />
            {t('invoice.reprint.title')}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t('invoice.reprint.alreadyPrinted', { batchNumber, lastPrintedAt: formattedDate })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 text-sm text-muted-foreground">
          {t('invoice.reprint.auditNote')}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
            className="h-12"
          >
            {t('invoice.reprint.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isConfirming}
            className="h-12"
          >
            {isConfirming ? t('invoice.printing') : t('invoice.reprint.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
